import { __extends } from "tslib";
var ProjectConfig = /** @class */ (function () {
    function ProjectConfig(configJson, config, timestamp, httpETag) {
        this.configJson = configJson;
        this.config = config;
        this.timestamp = timestamp;
        this.httpETag = httpETag;
    }
    ProjectConfig.equals = function (projectConfig1, projectConfig2) {
        // When both ETags are available, we don't need to check the JSON content.
        return projectConfig1.httpETag && projectConfig2.httpETag
            ? projectConfig1.httpETag === projectConfig2.httpETag
            : projectConfig1.configJson === projectConfig2.configJson;
    };
    ProjectConfig.prototype.with = function (timestamp) { return new ProjectConfig(this.configJson, this.config, timestamp, this.httpETag); };
    Object.defineProperty(ProjectConfig.prototype, "isEmpty", {
        get: function () { return !this.config; },
        enumerable: false,
        configurable: true
    });
    ProjectConfig.prototype.isExpired = function (expirationMs) {
        return this === ProjectConfig.empty || this.timestamp + expirationMs < ProjectConfig.generateTimestamp();
    };
    ProjectConfig.generateTimestamp = function () {
        return new Date().getTime();
    };
    ProjectConfig.serialize = function (config) {
        var _a, _b;
        return config.timestamp + "\n"
            + ((_a = config.httpETag) !== null && _a !== void 0 ? _a : "") + "\n"
            + ((_b = config.configJson) !== null && _b !== void 0 ? _b : "");
    };
    ProjectConfig.deserialize = function (value) {
        var separatorIndices = Array(2);
        var index = 0;
        for (var i = 0; i < separatorIndices.length; i++) {
            index = value.indexOf("\n", index);
            if (index < 0) {
                throw new Error("Number of values is fewer than expected.");
            }
            separatorIndices[i] = index++;
        }
        var endIndex = separatorIndices[0];
        var slice = value.substring(0, endIndex);
        var fetchTime = parseInt(slice);
        if (isNaN(fetchTime)) {
            throw new Error("Invalid fetch time: " + slice);
        }
        index = endIndex + 1;
        endIndex = separatorIndices[1];
        slice = value.substring(index, endIndex);
        var httpETag = slice.length > 0 ? slice : void 0;
        index = endIndex + 1;
        slice = value.substring(index);
        var config;
        var configJson;
        if (slice.length > 0) {
            try {
                config = new Config(JSON.parse(slice));
            }
            catch (_a) {
                throw new Error("Invalid config JSON content: " + slice);
            }
            configJson = slice;
        }
        return new ProjectConfig(configJson, config, fetchTime, httpETag);
    };
    ProjectConfig.serializationFormatVersion = "v2";
    ProjectConfig.empty = new ProjectConfig(void 0, void 0, 0, void 0);
    return ProjectConfig;
}());
export { ProjectConfig };
var Config = /** @class */ (function () {
    function Config(json) {
        var _this = this;
        var _a, _b;
        this.preferences = json.p != null ? new Preferences(json.p) : void 0;
        this.segments = (_b = (_a = json.s) === null || _a === void 0 ? void 0 : _a.map(function (item) { return new Segment(item); })) !== null && _b !== void 0 ? _b : [];
        this.settings = json.f != null
            ? Object.fromEntries(Object.entries(json.f).map(function (_a) {
                var key = _a[0], value = _a[1];
                return [key, new Setting(value, _this)];
            }))
            : {};
    }
    Object.defineProperty(Config.prototype, "salt", {
        get: function () { var _a; return (_a = this.preferences) === null || _a === void 0 ? void 0 : _a.salt; },
        enumerable: false,
        configurable: true
    });
    return Config;
}());
export { Config };
export var RedirectMode;
(function (RedirectMode) {
    RedirectMode[RedirectMode["No"] = 0] = "No";
    RedirectMode[RedirectMode["Should"] = 1] = "Should";
    RedirectMode[RedirectMode["Force"] = 2] = "Force";
})(RedirectMode || (RedirectMode = {}));
var Preferences = /** @class */ (function () {
    function Preferences(json) {
        this.baseUrl = json.u;
        this.redirectMode = json.r;
        this.salt = json.s;
    }
    return Preferences;
}());
export { Preferences };
var Segment = /** @class */ (function () {
    function Segment(json) {
        var _a, _b;
        this.name = json.n;
        this.conditions = (_b = (_a = json.r) === null || _a === void 0 ? void 0 : _a.map(function (item) { return new UserCondition(item); })) !== null && _b !== void 0 ? _b : [];
    }
    return Segment;
}());
export { Segment };
/** Setting type. */
export var SettingType;
(function (SettingType) {
    /** On/off type (feature flag). */
    SettingType[SettingType["Boolean"] = 0] = "Boolean";
    /** Text type. */
    SettingType[SettingType["String"] = 1] = "String";
    /** Whole number type. */
    SettingType[SettingType["Int"] = 2] = "Int";
    /** Decimal number type. */
    SettingType[SettingType["Double"] = 3] = "Double";
})(SettingType || (SettingType = {}));
var SettingValueContainer = /** @class */ (function () {
    function SettingValueContainer(json, hasUnwrappedValue) {
        if (hasUnwrappedValue === void 0) { hasUnwrappedValue = false; }
        this.value = !hasUnwrappedValue ? unwrapSettingValue(json.v) : json.v;
        this.variationId = json.i;
    }
    return SettingValueContainer;
}());
export { SettingValueContainer };
var Setting = /** @class */ (function (_super) {
    __extends(Setting, _super);
    function Setting(json, config) {
        var _a, _b, _c, _d, _e, _f;
        var _this = _super.call(this, json, json.t < 0) || this;
        _this.type = json.t;
        var identifierAttribute = "Identifier";
        _this.percentageOptionsAttribute = (_a = json.a) !== null && _a !== void 0 ? _a : identifierAttribute;
        _this.targetingRules = (_c = (_b = json.r) === null || _b === void 0 ? void 0 : _b.map(function (item) { return new TargetingRule(item, config); })) !== null && _c !== void 0 ? _c : [];
        _this.percentageOptions = (_e = (_d = json.p) === null || _d === void 0 ? void 0 : _d.map(function (item) { return new PercentageOption(item); })) !== null && _e !== void 0 ? _e : [];
        _this.configJsonSalt = (_f = config === null || config === void 0 ? void 0 : config.salt) !== null && _f !== void 0 ? _f : "";
        return _this;
    }
    Setting.fromValue = function (value) {
        return new Setting({
            t: -1,
            v: value,
        });
    };
    return Setting;
}(SettingValueContainer));
export { Setting };
var TargetingRule = /** @class */ (function () {
    function TargetingRule(json, config) {
        var _a, _b;
        this.conditions = (_b = (_a = json.c) === null || _a === void 0 ? void 0 : _a.map(function (item) {
            return item.u != null ? new UserCondition(item.u) :
                item.p != null ? new PrerequisiteFlagCondition(item.p) :
                    item.s != null ? new SegmentCondition(item.s, config) :
                        void 0;
        })) !== null && _b !== void 0 ? _b : [];
        this.then = json.p != null
            ? json.p.map(function (item) { return new PercentageOption(item); })
            : new SettingValueContainer(json.s);
    }
    return TargetingRule;
}());
export { TargetingRule };
var PercentageOption = /** @class */ (function (_super) {
    __extends(PercentageOption, _super);
    function PercentageOption(json) {
        var _this = _super.call(this, json) || this;
        _this.percentage = json.p;
        return _this;
    }
    return PercentageOption;
}(SettingValueContainer));
export { PercentageOption };
/** User Object attribute comparison operator used during the evaluation process. */
export var UserComparator;
(function (UserComparator) {
    /** IS ONE OF (cleartext) - It matches when the comparison attribute is equal to any of the comparison values. */
    UserComparator[UserComparator["IsOneOf"] = 0] = "IsOneOf";
    /** IS NOT ONE OF (cleartext) - It matches when the comparison attribute is not equal to any of the comparison values. */
    UserComparator[UserComparator["IsNotOneOf"] = 1] = "IsNotOneOf";
    /** CONTAINS ANY OF (cleartext) - It matches when the comparison attribute contains any comparison values as a substring. */
    UserComparator[UserComparator["ContainsAnyOf"] = 2] = "ContainsAnyOf";
    /** NOT CONTAINS ANY OF (cleartext) - It matches when the comparison attribute does not contain any comparison values as a substring. */
    UserComparator[UserComparator["NotContainsAnyOf"] = 3] = "NotContainsAnyOf";
    /** IS ONE OF (semver) - It matches when the comparison attribute interpreted as a semantic version is equal to any of the comparison values. */
    UserComparator[UserComparator["SemVerIsOneOf"] = 4] = "SemVerIsOneOf";
    /** IS NOT ONE OF (semver) - It matches when the comparison attribute interpreted as a semantic version is not equal to any of the comparison values. */
    UserComparator[UserComparator["SemVerIsNotOneOf"] = 5] = "SemVerIsNotOneOf";
    /** &lt; (semver) - It matches when the comparison attribute interpreted as a semantic version is less than the comparison value. */
    UserComparator[UserComparator["SemVerLess"] = 6] = "SemVerLess";
    /** &lt;= (semver) - It matches when the comparison attribute interpreted as a semantic version is less than or equal to the comparison value. */
    UserComparator[UserComparator["SemVerLessOrEquals"] = 7] = "SemVerLessOrEquals";
    /** &gt; (semver) - It matches when the comparison attribute interpreted as a semantic version is greater than the comparison value. */
    UserComparator[UserComparator["SemVerGreater"] = 8] = "SemVerGreater";
    /** &gt;= (semver) - It matches when the comparison attribute interpreted as a semantic version is greater than or equal to the comparison value. */
    UserComparator[UserComparator["SemVerGreaterOrEquals"] = 9] = "SemVerGreaterOrEquals";
    /** = (number) - It matches when the comparison attribute interpreted as a decimal number is equal to the comparison value. */
    UserComparator[UserComparator["NumberEquals"] = 10] = "NumberEquals";
    /** != (number) - It matches when the comparison attribute interpreted as a decimal number is not equal to the comparison value. */
    UserComparator[UserComparator["NumberNotEquals"] = 11] = "NumberNotEquals";
    /** &lt; (number) - It matches when the comparison attribute interpreted as a decimal number is less than the comparison value. */
    UserComparator[UserComparator["NumberLess"] = 12] = "NumberLess";
    /** &lt;= (number) - It matches when the comparison attribute interpreted as a decimal number is less than or equal to the comparison value. */
    UserComparator[UserComparator["NumberLessOrEquals"] = 13] = "NumberLessOrEquals";
    /** &gt; (number) - It matches when the comparison attribute interpreted as a decimal number is greater than the comparison value. */
    UserComparator[UserComparator["NumberGreater"] = 14] = "NumberGreater";
    /** &gt;= (number) - It matches when the comparison attribute interpreted as a decimal number is greater than or equal to the comparison value. */
    UserComparator[UserComparator["NumberGreaterOrEquals"] = 15] = "NumberGreaterOrEquals";
    /** IS ONE OF (hashed) - It matches when the comparison attribute is equal to any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveIsOneOf"] = 16] = "SensitiveIsOneOf";
    /** IS NOT ONE OF (hashed) - It matches when the comparison attribute is not equal to any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveIsNotOneOf"] = 17] = "SensitiveIsNotOneOf";
    /** BEFORE (UTC datetime) - It matches when the comparison attribute interpreted as the seconds elapsed since <see href="https://en.wikipedia.org/wiki/Unix_time">Unix Epoch</see> is less than the comparison value. */
    UserComparator[UserComparator["DateTimeBefore"] = 18] = "DateTimeBefore";
    /** AFTER (UTC datetime) - It matches when the comparison attribute interpreted as the seconds elapsed since <see href="https://en.wikipedia.org/wiki/Unix_time">Unix Epoch</see> is greater than the comparison value. */
    UserComparator[UserComparator["DateTimeAfter"] = 19] = "DateTimeAfter";
    /** EQUALS (hashed) - It matches when the comparison attribute is equal to the comparison value (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextEquals"] = 20] = "SensitiveTextEquals";
    /** NOT EQUALS (hashed) - It matches when the comparison attribute is not equal to the comparison value (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextNotEquals"] = 21] = "SensitiveTextNotEquals";
    /** STARTS WITH ANY OF (hashed) - It matches when the comparison attribute starts with any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextStartsWithAnyOf"] = 22] = "SensitiveTextStartsWithAnyOf";
    /** NOT STARTS WITH ANY OF (hashed) - It matches when the comparison attribute does not start with any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextNotStartsWithAnyOf"] = 23] = "SensitiveTextNotStartsWithAnyOf";
    /** ENDS WITH ANY OF (hashed) - It matches when the comparison attribute ends with any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextEndsWithAnyOf"] = 24] = "SensitiveTextEndsWithAnyOf";
    /** NOT ENDS WITH ANY OF (hashed) - It matches when the comparison attribute does not end with any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveTextNotEndsWithAnyOf"] = 25] = "SensitiveTextNotEndsWithAnyOf";
    /** ARRAY CONTAINS ANY OF (hashed) - It matches when the comparison attribute interpreted as a comma-separated list contains any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveArrayContainsAnyOf"] = 26] = "SensitiveArrayContainsAnyOf";
    /** ARRAY NOT CONTAINS ANY OF (hashed) - It matches when the comparison attribute interpreted as a comma-separated list does not contain any of the comparison values (where the comparison is performed using the salted SHA256 hashes of the values). */
    UserComparator[UserComparator["SensitiveArrayNotContainsAnyOf"] = 27] = "SensitiveArrayNotContainsAnyOf";
    /** EQUALS (cleartext) - It matches when the comparison attribute is equal to the comparison value. */
    UserComparator[UserComparator["TextEquals"] = 28] = "TextEquals";
    /** NOT EQUALS (cleartext) - It matches when the comparison attribute is not equal to the comparison value. */
    UserComparator[UserComparator["TextNotEquals"] = 29] = "TextNotEquals";
    /** STARTS WITH ANY OF (cleartext) - It matches when the comparison attribute starts with any of the comparison values. */
    UserComparator[UserComparator["TextStartsWithAnyOf"] = 30] = "TextStartsWithAnyOf";
    /** NOT STARTS WITH ANY OF (cleartext) - It matches when the comparison attribute does not start with any of the comparison values. */
    UserComparator[UserComparator["TextNotStartsWithAnyOf"] = 31] = "TextNotStartsWithAnyOf";
    /** ENDS WITH ANY OF (cleartext) - It matches when the comparison attribute ends with any of the comparison values. */
    UserComparator[UserComparator["TextEndsWithAnyOf"] = 32] = "TextEndsWithAnyOf";
    /** NOT ENDS WITH ANY OF (cleartext) - It matches when the comparison attribute does not end with any of the comparison values. */
    UserComparator[UserComparator["TextNotEndsWithAnyOf"] = 33] = "TextNotEndsWithAnyOf";
    /** ARRAY CONTAINS ANY OF (cleartext) - It matches when the comparison attribute interpreted as a comma-separated list contains any of the comparison values. */
    UserComparator[UserComparator["ArrayContainsAnyOf"] = 34] = "ArrayContainsAnyOf";
    /** ARRAY NOT CONTAINS ANY OF (cleartext) - It matches when the comparison attribute interpreted as a comma-separated list does not contain any of the comparison values. */
    UserComparator[UserComparator["ArrayNotContainsAnyOf"] = 35] = "ArrayNotContainsAnyOf";
})(UserComparator || (UserComparator = {}));
var UserCondition = /** @class */ (function () {
    function UserCondition(json) {
        var _a, _b;
        this.type = "UserCondition";
        this.comparisonAttribute = json.a;
        this.comparator = json.c;
        this.comparisonValue = (_b = (_a = json.s) !== null && _a !== void 0 ? _a : json.d) !== null && _b !== void 0 ? _b : json.l;
    }
    return UserCondition;
}());
export { UserCondition };
/** Prerequisite flag comparison operator used during the evaluation process. */
export var PrerequisiteFlagComparator;
(function (PrerequisiteFlagComparator) {
    /** EQUALS - It matches when the evaluated value of the specified prerequisite flag is equal to the comparison value. */
    PrerequisiteFlagComparator[PrerequisiteFlagComparator["Equals"] = 0] = "Equals";
    /** NOT EQUALS - It matches when the evaluated value of the specified prerequisite flag is not equal to the comparison value. */
    PrerequisiteFlagComparator[PrerequisiteFlagComparator["NotEquals"] = 1] = "NotEquals";
})(PrerequisiteFlagComparator || (PrerequisiteFlagComparator = {}));
var PrerequisiteFlagCondition = /** @class */ (function () {
    function PrerequisiteFlagCondition(json) {
        this.type = "PrerequisiteFlagCondition";
        this.prerequisiteFlagKey = json.f;
        this.comparator = json.c;
        this.comparisonValue = unwrapSettingValue(json.v);
    }
    return PrerequisiteFlagCondition;
}());
export { PrerequisiteFlagCondition };
/** Segment comparison operator used during the evaluation process. */
export var SegmentComparator;
(function (SegmentComparator) {
    /** IS IN SEGMENT - It matches when the conditions of the specified segment are evaluated to true. */
    SegmentComparator[SegmentComparator["IsIn"] = 0] = "IsIn";
    /** IS NOT IN SEGMENT - It matches when the conditions of the specified segment are evaluated to false. */
    SegmentComparator[SegmentComparator["IsNotIn"] = 1] = "IsNotIn";
})(SegmentComparator || (SegmentComparator = {}));
var SegmentCondition = /** @class */ (function () {
    function SegmentCondition(json, config) {
        this.type = "SegmentCondition";
        this.segment = config.segments[json.s];
        this.comparator = json.c;
    }
    return SegmentCondition;
}());
export { SegmentCondition };
function unwrapSettingValue(json) {
    var _a, _b, _c;
    return (_c = (_b = (_a = json.b) !== null && _a !== void 0 ? _a : json.s) !== null && _b !== void 0 ? _b : json.i) !== null && _c !== void 0 ? _c : json.d;
}
